'use client'

import { useEffect, useState } from 'react'

/**
 * 60s tick (pause while tab hidden) — returns `null` on the server and during
 * the initial client render so callers can skip hydration-sensitive markup
 * (time-based indicators otherwise produce a hydration mismatch warning).
 * The first real Date arrives in `useEffect`; the interval keeps it fresh.
 *
 * Originally lived inside MeetingsNowCard; lifted out so the /meetings table
 * time-indicator can share the same tick cadence + tab-hidden semantics.
 */
export function useNowTick(intervalMs = 60_000): Date | null {
  const [now, setNow] = useState<Date | null>(null)
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null
    const start = () => {
      if (id !== null) return
      setNow(new Date())
      id = setInterval(() => setNow(new Date()), intervalMs)
    }
    const stop = () => {
      if (id === null) return
      clearInterval(id)
      id = null
    }
    start()
    const onVisibility = () =>
      document.visibilityState === 'visible' ? start() : stop()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs])
  return now
}
